import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { getAnalysisQueue } from '../../../../../../../api/theHive/licensedCompanies';
import {
  AnalysisQueueItem,
  AnalysisQueueName,
} from '../../../../../../../api/theHive/licensedCompanies/types';
import { Constants } from '../../../../../../../constants/hiveNotices';
import {
  IconCloseMS,
  IconSearchMS,
} from '../../../../../../../constants/icons';
import {
  formatCnpjCpf,
  formatLocalDateTime,
} from '../../../../../../../helpers';
import { AccessKey, canSee } from '../../../../../../../helpers/roleAccess';
import { useCurrentRole } from '../../../../../../../hooks/useCurrentRole';
import { useDebounce } from '../../../../../../../hooks/useDebounce';
import useErrorMessage from '../../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../../hooks/useSnackbar';
import { CommentsCell } from '../../../components/CompanyComments/CommentsCell';
import { CommentsDrawer } from '../../../components/CompanyComments/CommentsDrawer';
import {
  ClearSearchButton,
  SearchInput,
  StyledCell,
  StyledPagination,
  StyledRow,
  TableCellHeader,
} from '../styles';
import { AnalysisStatusIcon } from './AnalysisStatusIcon';

const PER_PAGE = 100;

interface QueueTabOption {
  value: AnalysisQueueName;
  label: string;
  accessKey: AccessKey;
}

const QUEUE_TABS: QueueTabOption[] = [
  {
    value: 'juridico',
    label: 'Jurídico',
    accessKey: 'analysisQueueJuridico',
  },
  {
    value: 'engenharia',
    label: 'Engenharia',
    accessKey: 'analysisQueueEngenharia',
  },
  {
    value: 'contrato',
    label: 'Contrato',
    accessKey: 'analysisQueueContrato',
  },
];

// single source for "which queues this role sees" — the parent view toggle
// derives its visibility from here too, so registering a new queue is one
// edit in QUEUE_TABS
export const visibleQueueTabs = (role: number | null): QueueTabOption[] =>
  QUEUE_TABS.filter((tab) => canSee(tab.accessKey, role));

// Per-axis priority queues (PROT-2950): the queue IS the order — P1
// never-reviewed before P2 resubmitted, tie broken by datetime across
// pages — so there is no sort control here on purpose.
export function AnalysisQueues(): JSX.Element {
  const role = useCurrentRole();
  const visibleTabs = visibleQueueTabs(role);

  const [items, setItems] = useState<AnalysisQueueItem[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [commentsTarget, setCommentsTarget] =
    useState<AnalysisQueueItem | null>(null);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();
  const navigate = useNavigate();
  const { notice: noticeIdParam } = useParams();
  const noticeId = parseInt(noticeIdParam as string, 10);

  // Queue, page and search live in the URL to survive returning from the
  // analysis screen.
  const [searchParams, setSearchParams] = useSearchParams();
  const queueFromUrl = searchParams.get('queue');
  const queue: AnalysisQueueName =
    visibleTabs.find((tab) => tab.value === queueFromUrl)?.value ??
    visibleTabs[0]?.value ??
    'juridico';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const searchFromUrl = searchParams.get('search') || '';

  const [searchTerm, setSearchTerm] = useState(searchFromUrl);
  const debouncedSearch = useDebounce(searchTerm);

  const applySearchToUrl = (value: string): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set('search', value);
        else next.delete('search');
        next.delete('page');
        return next;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (debouncedSearch !== searchFromUrl) applySearchToUrl(debouncedSearch);
    // searchFromUrl/applySearchToUrl excluded: the URL update they'd trigger
    // is what moves searchFromUrl, so including them reruns this every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleClearSearch = (): void => {
    setSearchTerm('');
    applySearchToUrl('');
  };

  const handleQueueChange = (
    _event: React.SyntheticEvent,
    newQueue: AnalysisQueueName
  ): void => {
    setSearchTerm('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('queue', newQueue);
      next.delete('page');
      next.delete('search');
      return next;
    });
  };

  const handleNavigate = (companyId: number): void => {
    navigate(`./request/${companyId}`);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(value));
      return next;
    });
  };

  // ignore superseded responses: a slow fetch from the previous tab/page
  // must never render under the current tab's header ("the queue IS the
  // order" — showing another queue's rows here is actively harmful)
  const runRef = useRef(0);

  const fetchQueue = useCallback(async (): Promise<void> => {
    const runId = runRef.current + 1;
    runRef.current = runId;

    setLoading(true);
    try {
      const response = await getAnalysisQueue(
        noticeId,
        queue,
        page,
        PER_PAGE,
        searchFromUrl
      );
      if (runRef.current !== runId) return;
      if (response instanceof Error) throw response;
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      setItems(response.data ?? []);
      setTotalPages(response.detail.total_pages ?? 1);
    } catch (error) {
      if (runRef.current !== runId) return;
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      if (runRef.current === runId) setLoading(false);
    }
    // handleSnackbar/getErrorMessage are recreated every render (not
    // memoized) — depending on them would refetch on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noticeId, queue, page, searchFromUrl]);

  useEffect(() => {
    if (!Number.isNaN(noticeId) && visibleTabs.length > 0) fetchQueue();
    // visibleTabs.length covers role/JWT resolving after the first render
  }, [fetchQueue, noticeId, visibleTabs.length]);

  const searchDisabled = !searchFromUrl && !loading && items.length === 0;
  const isContractQueue = queue === 'contrato';

  const renderPriority = (item: AnalysisQueueItem): JSX.Element => (
    <Tooltip
      placement="top"
      title={
        item.priority === 1
          ? 'Nunca avaliado — frente da fila'
          : 'Resubmetido após correção'
      }
    >
      <Chip
        color={item.priority === 1 ? 'secondary' : 'default'}
        label={item.priority === 1 ? 'P1 · nunca avaliado' : 'P2 · resubmetido'}
        size="small"
        variant="outlined"
      />
    </Tooltip>
  );

  const renderRows = (): JSX.Element => {
    if (loading) {
      return (
        <StyledRow>
          <StyledCell colSpan={7} align="center">
            <CircularProgress size={24} />
          </StyledCell>
        </StyledRow>
      );
    }

    if (items.length === 0) {
      return (
        <StyledRow>
          <StyledCell colSpan={7} align="center">
            {searchFromUrl
              ? Constants.noRequestsFound
              : 'Nenhum pedido aguardando análise nesta fila.'}
          </StyledCell>
        </StyledRow>
      );
    }

    return (
      <>
        {items.map((item, index) => (
          <StyledRow
            key={item.notice_company_id}
            onClick={() => handleNavigate(item.notice_company_id)}
          >
            <StyledCell align="center">
              {(page - 1) * PER_PAGE + index + 1}º
            </StyledCell>
            <StyledCell>{item.company_name}</StyledCell>
            <StyledCell align="center">{formatCnpjCpf(item.cnpj)}</StyledCell>
            <StyledCell align="center">{renderPriority(item)}</StyledCell>
            <StyledCell align="center">
              {formatLocalDateTime(item.queue_at)}
            </StyledCell>
            <StyledCell align="center">
              <AnalysisStatusIcon
                status={item.axis_status ?? 'pending'}
                axis={isContractQueue ? 'contract' : 'documents'}
                submittedAt={item.formalization_submitted_at}
                pendingScopeTerms={item.pending_scope_terms_count}
              />
            </StyledCell>
            <StyledCell align="center">
              <CommentsCell
                count={item.comments_count ?? 0}
                disabled={!item.licensed_company_id}
                onOpen={() => setCommentsTarget(item)}
              />
            </StyledCell>
          </StyledRow>
        ))}
      </>
    );
  };

  return (
    <>
      <Box
        alignItems="center"
        display="flex"
        justifyContent="space-between"
        mb={2}
      >
        <Tabs onChange={handleQueueChange} value={queue}>
          {visibleTabs.map((tab) => (
            <Tab
              key={tab.value}
              label={tab.label}
              sx={{ textTransform: 'none' }}
              value={tab.value}
            />
          ))}
        </Tabs>
        <SearchInput
          placeholder={Constants.searchCompany}
          inputProps={{ 'aria-label': 'pesquisar', maxLength: 60 }}
          value={searchTerm}
          disabled={searchDisabled}
          onChange={(e) => setSearchTerm(e.target.value)}
          startAdornment={
            <InputAdornment position="start">{IconSearchMS}</InputAdornment>
          }
          endAdornment={
            searchTerm ? (
              <InputAdornment position="end">
                <ClearSearchButton
                  aria-label={Constants.clearSearch}
                  onClick={handleClearSearch}
                >
                  {IconCloseMS}
                </ClearSearchButton>
              </InputAdornment>
            ) : null
          }
        />
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCellHeader align="center" width="7%">
                Ordem
              </TableCellHeader>
              <TableCellHeader width="26%">Nome da empresa</TableCellHeader>
              <TableCellHeader align="center" width="15%">
                CNPJ
              </TableCellHeader>
              <TableCellHeader align="center" width="16%">
                Prioridade
              </TableCellHeader>
              <TableCellHeader align="center" width="16%">
                Aguardando desde
              </TableCellHeader>
              <TableCellHeader align="center" width="12%">
                {isContractQueue ? 'Contrato' : 'Documentos'}
              </TableCellHeader>
              <TableCellHeader align="center" width="8%">
                Notas
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>{renderRows()}</TableBody>
        </Table>
      </TableContainer>
      <StyledPagination
        page={page}
        count={totalPages}
        onChange={handlePageChange}
      />
      <CommentsDrawer
        open={!!commentsTarget}
        onClose={() => setCommentsTarget(null)}
        noticeId={noticeId}
        licensedCompanyId={commentsTarget?.licensed_company_id ?? undefined}
        companyName={commentsTarget?.company_name ?? ''}
      />
    </>
  );
}
